import { nanoid } from "nanoid";
import envVariables from "src/lib/envVariables";
import reportErrorSentry from "src/lib/reportErrorSentry";
import type { DeviceInfo } from "../types";

const WEB_DEVICE_UUID_KEY = "device-uuid";

/**
 * Collects device information for web platform
 * Returns limited device info with fallbacks for native-only properties
 */
export async function collectWebInfo(): Promise<DeviceInfo> {
  return {
    // System Information
    platform: "web",
    operatingSystem: "web",
    osVersion: navigator.userAgent,
    appVersion: envVariables.BUILD_VERSION,
    appBuild: "web",
    webViewVersion: "unavailable",
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    // Hardware (unavailable on web)
    manufacturer: "unavailable",
    model: "unavailable",
    deviceId: getWebDeviceId(),
    isVirtual: false,

    // Network
    networkConnected: navigator.onLine,
    networkType: "unknown",

    // Permissions (unavailable on web)
    pushNotificationStatus: "prompt",
    biometricAvailable: false
  };
}

/**
 * Gets or generates a persistent device UUID for web platform
 * Stored in localStorage to persist across sessions
 */
function getWebDeviceId(): string {
  try {
    let uuid = localStorage.getItem(WEB_DEVICE_UUID_KEY);

    if (!uuid) {
      uuid = nanoid();
      localStorage.setItem(WEB_DEVICE_UUID_KEY, uuid);
    }

    return uuid;
  } catch (error) {
    // If localStorage is unavailable (e.g., private browsing), generate temporary ID
    reportErrorSentry(error);
    return nanoid();
  }
}
